import { Vec3 } from './vec3';

/**
 * Damped spring step. Returns new [value, velocity]. Semi-implicit Euler.
 * stiffness ~ (2*pi*freq)^2 ; damping ~ 2*zeta*sqrt(stiffness).
 */
export function spring(current: number, target: number, velocity: number, stiffness: number, damping: number, dt: number): [number, number] {
  const a = -stiffness * (current - target) - damping * velocity;
  velocity += a * dt;
  current += velocity * dt;
  return [current, velocity];
}

/** Scalar spring with state; read `value`, set `target`. */
export class SpringF {
  velocity = 0;
  constructor(public value = 0, public target = value, public stiffness = 200, public damping = 20) {}
  static fromFreq(value: number, freqHz: number, zeta: number): SpringF {
    return new SpringF(value).setFreq(freqHz, zeta);
  }
  setFreq(freqHz: number, zeta: number): this {
    const w = 2 * Math.PI * freqHz;
    this.stiffness = w * w;
    this.damping = 2 * zeta * w;
    return this;
  }
  update(dt: number): number {
    const steps = this.stiffness > 4000 ? 4 : this.stiffness > 1000 ? 2 : 1;
    const h = dt / steps;
    for (let i = 0; i < steps; i++) {
      const a = -this.stiffness * (this.value - this.target) - this.damping * this.velocity;
      this.velocity += a * h;
      this.value += this.velocity * h;
    }
    return this.value;
  }
  kick(v: number): this { this.velocity += v; return this; }
  snap(v = this.target): this { this.value = v; this.target = v; this.velocity = 0; return this; }
  get settled(): boolean { return Math.abs(this.value - this.target) < 1e-4 && Math.abs(this.velocity) < 1e-4; }
}

export class SpringV3 {
  velocity = new Vec3();
  value: Vec3;
  target: Vec3;
  constructor(value = new Vec3(), public stiffness = 200, public damping = 20) {
    this.value = value.clone();
    this.target = value.clone();
  }
  setFreq(freqHz: number, zeta: number): this {
    const w = 2 * Math.PI * freqHz;
    this.stiffness = w * w;
    this.damping = 2 * zeta * w;
    return this;
  }
  update(dt: number): Vec3 {
    const steps = this.stiffness > 4000 ? 4 : this.stiffness > 1000 ? 2 : 1;
    const h = dt / steps;
    const v = this.value, vel = this.velocity, t = this.target;
    for (let i = 0; i < steps; i++) {
      vel.x += (-this.stiffness * (v.x - t.x) - this.damping * vel.x) * h;
      vel.y += (-this.stiffness * (v.y - t.y) - this.damping * vel.y) * h;
      vel.z += (-this.stiffness * (v.z - t.z) - this.damping * vel.z) * h;
      v.x += vel.x * h; v.y += vel.y * h; v.z += vel.z * h;
    }
    return v;
  }
  kick(v: Vec3): this { this.velocity.add(v); return this; }
  snap(v = this.target): this { this.value.copy(v); this.target.copy(v); this.velocity.set(0, 0, 0); return this; }
}
